/**
 * Blockchain Transactions
 * Doc: docs/overall-plan/01-CORE-SYSTEMS/blockchain-transactions/
 * Placeholder page wired to GET /api/v1/blockchain/transactions (stub returns empty list).
 */
import React, { useEffect, useState } from 'react';
import { blockchainApi } from '../services/api';
import { showToast, askFields } from 'shared-ui';

const Blockchain: React.FC = () => {
  const [transactions, setTransactions] = useState<unknown[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recordStatus, setRecordStatus] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ id: string; verified?: boolean; message?: string } | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [detailTx, setDetailTx] = useState<{ id: string; type: string; amount?: number; referenceId?: string; onChainTxHash?: string; createdAt: string } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshingList, setRefreshingList] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [summary, setSummary] = useState<{ count: number; totalAmount: number } | null>(null);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [filterType, setFilterType] = useState<string>('');
  const [typeOptions, setTypeOptions] = useState<string[]>(['RECORD', 'TRANSFER']);

  const fetchSummary = (typeFilter?: string) =>
    blockchainApi.getSummary(typeFilter ?? (filterType.trim() || undefined)).then((res) => {
      if (res?.success && res?.data && typeof res.data.count === 'number') {
        setSummary({ count: res.data.count, totalAmount: typeof res.data.totalAmount === 'number' ? res.data.totalAmount : 0 });
      }
    });

  const fetchTransactions = (limit = 50, offset = 0) =>
    blockchainApi.getTransactions(limit, offset).then((res) => {
      if (res?.success && Array.isArray(res?.data)) {
        setTransactions(res.data);
        if (typeof res?.total === 'number') setTotal(res.total);
      }
    });

  const handleRecordStub = async () => {
    const values = await askFields({
      title: 'Record a transaction',
      confirmLabel: 'Record',
      fields: [
        { key: 'type', label: 'Transaction type', defaultValue: 'RECORD', placeholder: 'e.g. RECORD, TRANSFER' },
        { key: 'amount', label: 'Amount', type: 'number' },
        { key: 'referenceId', label: 'Reference ID' },
      ],
    });
    if (!values) return;
    const type = values.type || 'RECORD';
    const amount = values.amount ? parseFloat(values.amount) : undefined;
    if (amount !== undefined && Number.isNaN(amount)) {
      showToast('Amount must be a number', 'error');
      return;
    }
    const referenceId = values.referenceId || undefined;
    setRecording(true);
    setRecordStatus(null);
    setVerifyResult(null);
    blockchainApi.recordTransaction({ type, amount, referenceId })
      .then((res) => {
        if (res?.success) {
          setRecordStatus('Recorded.');
          return Promise.all([fetchTransactions(), fetchSummary()]);
        }
        setRecordStatus(res?.error || 'Record failed');
      })
      .catch(() => setRecordStatus('Record failed'))
      .finally(() => setRecording(false));
  };

  const handleRefresh = () => {
    setRefreshingList(true);
    Promise.all([fetchTransactions(), fetchSummary()]).finally(() => setRefreshingList(false));
  };

  const handleLoadMore = () => {
    const offset = transactions.length;
    if (offset >= (total ?? 0)) return;
    setLoadMoreLoading(true);
    const typeParam = filterType.trim() || undefined;
    blockchainApi.getTransactions(50, offset, typeParam).then((res) => {
      if (res?.success && Array.isArray(res?.data)) {
        setTransactions((prev) => [...prev, ...res.data]);
        if (typeof res?.total === 'number') setTotal(res.total);
      }
      setLoadMoreLoading(false);
    }).catch(() => setLoadMoreLoading(false));
  };

  const handleViewDetail = (txId: string) => {
    setDetailLoading(true);
    setDetailTx(null);
    blockchainApi.getTransaction(txId)
      .then((res) => {
        if (res?.success && res?.data) setDetailTx(res.data);
        else setDetailTx(null);
      })
      .catch(() => setDetailTx(null))
      .finally(() => setDetailLoading(false));
  };

  const handleVerify = (txId: string) => {
    setVerifyingId(txId);
    setVerifyResult(null);
    blockchainApi.verifyTransaction(txId)
      .then((res) => {
        if (res?.success && res?.data) {
          setVerifyResult({ id: txId, verified: res.data.verified, message: res.data.message });
        } else {
          setVerifyResult({ id: txId, verified: false, message: res?.data?.message || res?.error || 'Verify failed' });
        }
      })
      .catch(() => setVerifyResult({ id: txId, verified: false, message: 'Verify failed' }))
      .finally(() => setVerifyingId(null));
  };

  useEffect(() => {
    let cancelled = false;
    blockchainApi.getTransactionTypes().then((res) => {
      if (!cancelled && res?.success && Array.isArray(res?.data) && res.data.length > 0) {
        setTypeOptions(res.data);
      }
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const typeParam = filterType.trim() || undefined;
    Promise.all([
      blockchainApi.getTransactions(50, 0, typeParam),
      blockchainApi.getSummary(typeParam),
    ])
      .then(([listRes, summaryRes]) => {
        if (cancelled) return;
        if (listRes?.success) {
          setTransactions(Array.isArray(listRes?.data) ? listRes.data : []);
          if (typeof listRes?.total === 'number') setTotal(listRes.total);
          if (listRes?.message) setMessage(listRes.message);
        } else setError(listRes?.error || 'Failed to load transactions');
        if (summaryRes?.success && summaryRes?.data && typeof summaryRes.data.count === 'number') {
          setSummary({
            count: summaryRes.data.count,
            totalAmount: typeof summaryRes.data.totalAmount === 'number' ? summaryRes.data.totalAmount : 0,
          });
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load transactions');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [filterType]);

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #0f766e 0%, #134e4a 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>⛓️ Blockchain</h1>
        <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
          Verified on-chain transaction history
        </p>
        {summary != null && (
          <p style={{ margin: '12px 0 0 0', fontSize: '13px', opacity: 0.9 }}>
            {filterType.trim() ? `${filterType}: ` : ''}{summary.count} transaction{summary.count !== 1 ? 's' : ''}
            {summary.totalAmount !== 0 && ` · Total amount: ${summary.totalAmount}`}
          </p>
        )}
      </div>
      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {message && <p style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>{message}</p>}
      {recordStatus && <p style={{ marginBottom: '16px', fontSize: '14px', color: '#059669' }}>{recordStatus}</p>}
      {verifyResult && (
        <p style={{ marginBottom: '16px', fontSize: '14px', color: verifyResult.verified ? '#059669' : '#b91c1c' }}>
          {verifyResult.verified ? '✓ Verified' : '✗ Not verified'}: {verifyResult.message}
        </p>
      )}
      {detailLoading && <p style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>Loading detail…</p>}
      {detailTx && !detailLoading && (
        <div style={{ marginBottom: '16px', padding: '16px', background: '#f0fdfa', borderRadius: '12px', border: '1px solid #99f6e4' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <strong style={{ color: '#0f766e' }}>Transaction detail</strong>
            <button type="button" onClick={() => setDetailTx(null)} style={{ padding: '4px 10px', fontSize: '12px', cursor: 'pointer', border: '1px solid #0f766e', borderRadius: '6px', background: 'transparent', color: '#0f766e' }}>Close</button>
          </div>
          <p style={{ margin: '4px 0', fontSize: '14px' }}>ID: {detailTx.id}</p>
          <p style={{ margin: '4px 0', fontSize: '14px' }}>Type: {detailTx.type}</p>
          {detailTx.amount != null && <p style={{ margin: '4px 0', fontSize: '14px' }}>Amount: {detailTx.amount}</p>}
          {detailTx.referenceId && <p style={{ margin: '4px 0', fontSize: '14px' }}>Reference: {detailTx.referenceId}</p>}
          {detailTx.onChainTxHash && (
            <p style={{ margin: '4px 0', fontSize: '13px', color: '#0f766e', wordBreak: 'break-all' }}>On-chain: {detailTx.onChainTxHash}</p>
          )}
          <p style={{ margin: '4px 0', fontSize: '14px', color: '#64748b' }}>Created: {detailTx.createdAt}</p>
        </div>
      )}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleRecordStub}
          disabled={recording}
          style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #0f766e', background: '#0f766e', color: 'white', cursor: recording ? 'not-allowed' : 'pointer', fontWeight: 600 }}
        >
          {recording ? 'Recording…' : 'Record'}
        </button>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshingList}
          style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #64748b', background: 'transparent', color: '#64748b', cursor: refreshingList ? 'not-allowed' : 'pointer', fontWeight: 600 }}
        >
          {refreshingList ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <label htmlFor="blockchain-type-filter" style={{ fontSize: '14px', color: '#64748b' }}>Filter by type:</label>
        <select
          id="blockchain-type-filter"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', background: 'white' }}
        >
          <option value="">All</option>
          {typeOptions.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      {transactions.length === 0 ? (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '14px',
        }}>
          No blockchain transactions yet. Wallet, orders, and payments will appear here when recording is enabled.
        </div>
      ) : (
        <>
          {total != null && (
            <p style={{ marginBottom: '8px', fontSize: '13px', color: '#64748b' }}>
              Showing {transactions.length} of {total} transaction{total !== 1 ? 's' : ''}
            </p>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {transactions.map((tx: any, i: number) => (
            <li key={tx?.id ?? i} style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontWeight: 600 }}>{tx?.type ?? '—'}</span>
              {tx?.amount != null && <span style={{ color: '#64748b' }}>{tx.amount}</span>}
              {tx?.referenceId && <span style={{ color: '#64748b' }}>ref: {tx.referenceId}</span>}
              {tx?.onChainTxHash && <span style={{ color: '#0f766e', fontSize: '12px' }} title={tx.onChainTxHash}>⛓️</span>}
              <span style={{ color: '#94a3b8', fontSize: '12px' }}>{tx?.id} · {tx?.createdAt}</span>
              {tx?.id && (
                <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                  <button
                    type="button"
                    onClick={() => handleViewDetail(tx.id)}
                    disabled={detailLoading}
                    style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #64748b', background: 'transparent', color: '#64748b', cursor: detailLoading ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    onClick={() => handleVerify(tx.id)}
                    disabled={verifyingId === tx.id}
                    style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #0f766e', background: 'transparent', color: '#0f766e', cursor: verifyingId === tx.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                  >
                    {verifyingId === tx.id ? 'Verifying…' : 'Verify'}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
          {total != null && transactions.length < total && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loadMoreLoading}
                style={{ padding: '8px 20px', borderRadius: '8px', border: '1px solid #64748b', background: 'transparent', color: '#64748b', cursor: loadMoreLoading ? 'not-allowed' : 'pointer', fontSize: '14px' }}
              >
                {loadMoreLoading ? 'Loading…' : `Load more (${transactions.length} of ${total})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Blockchain;
